import type {
    CatchParams,
    FetchParams, FetchReturnType,
    ValidationInternalData,
    ValidationResponse,
    ValidationRules,
} from '@/app';
import { SModal } from '@/app';
import { ___, __const } from '@/app/_internal/helpers';

type Value = string | string[];
type Key = string | string[] | null;

export class g {
    static errorModalIsShowed: boolean = false;

    static resolveElement(target: string | Element | undefined, name?: string, context: string = 'g'): Element {
        const targetName = name ?? 'element';

        if (target === undefined) {
            throw new Error(`[${context}] The "${targetName}" parameter is required.`);
        }

        const el = typeof target === 'string' ? document.querySelector(target) : target;
        if (!el) {
            const details = typeof target === 'string' ? ` with selector: "${target}".` : '.';
            const notFoundMessage = name
                ? `No element found for "${targetName}"${details}`
                : `No element found${details}`;
            throw new Error(`[${context}] ${notFoundMessage}`);
        }

        return el;
    }

    static isNotNull(variable: any): boolean {
        return variable !== null;
    }

    static isObject(variable: any): boolean {
        return typeof variable === 'object' && variable !== null;
    }

    static isUndefined(variable: any): boolean {
        return typeof variable === 'undefined';
    }

    static escapeHtml(html: string): string {
        return html
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll('\'', '&#039;');
    }

    static handleGlobalError(event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) {
        // console.log({event, source, lineno, colno, error,});
        // console.log('--------------');

        let message = error?.message;
        if (message === undefined && typeof event === 'string') {
            message = event;
        }
        if (message === undefined) {
            message = 'No se ha podido obtener ningun mensaje del error';
        }

        console.warn(message);
        // console.warn({event, source, lineno, colno, error});

        // TODO Canals - mirgrar a Tingle para no pisar otro modal que este abierto: alert(g.escapeHtml(error?.message ?? "Formato error imprevisto"));
        SModal.error({
            title: 'Error imprevisto',
            html: `<span class="restriction-message">${g.escapeHtml(message)}</span>`,
            cancelButtonText: 'Ok',
        }).then(result => {
            g.errorModalIsShowed = false;
        }).catch(e => {
            //
        });
        g.errorModalIsShowed = true;
        return false;
    }

    private static async fetchBase<R>(
        { url, type = 'GET', params = undefined, headers = {} }: FetchParams,
        strict: boolean
    ): Promise<FetchReturnType<R>>
    {
        try {
            const isFormData = params instanceof FormData;

            const finalHeaders: Record<string, string> = {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': __const('token') ?? '',
                ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
                ...headers,
            };

            const spoofedMethods = ['PUT', 'PATCH', 'DELETE'] as const;
            const needsSpoofing = isFormData && (spoofedMethods as readonly string[]).includes(type);

            if (needsSpoofing && params instanceof FormData) {
                params.append('_method', type);
            }

            const fetchParams: RequestInit = {
                method: needsSpoofing ? 'POST' : type,
                headers: finalHeaders,
            };

            if (type !== 'GET' && params !== undefined) {
                fetchParams.body = isFormData ? params : JSON.stringify(params);
            }

            const response = await fetch(url, fetchParams);
            const result = await response.json() as FetchReturnType<R> | string;

            if (typeof result === 'string') {
                const reason = result === '' ? 'The server returned an empty response.' : result;
                return Promise.reject(reason);
            }

            result.ok = response.ok;
            if (strict && !response.ok) {
                return Promise.reject(result);
            }

            return Promise.resolve(result);
        } catch (e) {
            console.error('Fetch request failed.');
            return Promise.reject(e);
        }
    }

    static async fetch<R>({url, type = 'GET', params = undefined, headers = {}}: FetchParams): Promise<FetchReturnType<R>>
    {
        return await g.fetchBase<R>({url, type, params, headers}, false);
    }

    static async fetchStrict<R>({url, type = 'GET', params = undefined, headers = {}}: FetchParams): Promise<FetchReturnType<R>>
    {
        return await g.fetchBase<R>({url, type, params, headers}, true);
    }

    static async fetchString({url, type = 'GET', params = undefined}: FetchParams): Promise<{ok: boolean, text: string}>
    {
        try {
            let fetchParams = {
                method: type, // *GET, POST, PUT, DELETE, etc.
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': __const('token'),
                },
            } as RequestInit;

            if (type !== 'GET' && params !== undefined) {
                fetchParams.body = JSON.stringify(params);
            }

            const response = await fetch(url, fetchParams);
            const text = await response.text();

            return Promise.resolve({
                ok: response.ok,
                text: text,
            });
        } catch (e) {
            console.error('Error con fetch');
            return Promise.reject(e);
        }
    }

    static catchCode({
                         error,
                         title = undefined,
                         text = undefined,
                         html = undefined,
                         reloadOnClose = false,
                         footer = undefined,
                         from = undefined,
                     }: CatchParams)
    {
        console.error(error);

        if (g.errorModalIsShowed) return;

        if (!g.isUndefined(from)) {
            console.log('From:', from);
        }

        const message = g.getErrorMessage(text, error);
        const finalHtml = html
            ? html
            : `<span class="restriction-message">${g.escapeHtml(message)}</span>`;

        // Abrir modal
        SModal.error({
            icon: 'error',
            title: title,
            html: finalHtml,
            confirmButtonText: (reloadOnClose) ? ___('reload_page') : ___('ok'),
            footer: footer,
        }, true)
            .then((result) => {
                g.errorModalIsShowed = false;
                if (reloadOnClose && result.isConfirmed) {
                    location.reload();
                }
            });
        g.errorModalIsShowed = true;
    }

    private static getErrorMessage(text: string|undefined, error: any): string
    {
        if (text !== undefined) {
            return text;
        }
        if (!g.isObject(error)) {
            return error;
        }

        return error.hasOwnProperty('message') ? error.message : 'Error imprevisto';
    }

    static sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    static strIsEmpty(str: any) {
        if (typeof str !== 'string') return false;
        return !Boolean(str.trim());
    }

    static isEmpty(value: any) {
        return g.isNullish(value) || g.strIsEmpty(value) || Number.isNaN(value);
    }

    static deleteArrayItemsWhere(array: any[], filterFunc: (e: any) => boolean, nameField: string) {
        let deleteItems = array.filter(filterFunc);
        deleteItems.forEach(deleteItem => array.splice(array.findIndex(item => item[nameField] === deleteItem[nameField]), 1));
        return array;
    }

    static valueFailsRequiredValidation(value: any) {
        // return (!value && value !== 0) || value.length === 0 || /^\s*$/.test(value)
        return value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0);
    }

    static validate(data: Record<string, any>, rules: ValidationRules): ValidationResponse {
        // creamos el objeto de respuesta
        let res: ValidationInternalData = {success: [], messages: [], validated: {}};

        // Recorremos el objeto de Reglas de valicacion
        Object.entries(rules).forEach(([key, value]) => {

            if (!Array.isArray(value)) {
                res.validated[key] = value;
                return;
            }

            // Recorremos las reglas de la validacion acual
            value.forEach(item => { // item = required, index = 0

                // Separar la regla de los parametros
                let arrayItems = item.split(':');

                // Logica segun el tipo de validacion
                switch (arrayItems[0]) {
                    case 'optional':
                        if (data.hasOwnProperty(key) && data[key] !== undefined) {
                            res.validated[key] = data[key];
                        } else {
                            res.validated[key] = null;
                        }
                        break;
                    case 'required':
                        if (g.valueFailsRequiredValidation(data[key])) {
                            res.success.push(false);
                            res.messages.push(`El campo "${key}" es obligatorio`);
                        } else {
                            res.validated[key] = data[key];
                        }
                        break;
                    case 'min':
                        if (data[key]) {
                            if (data[key].length < parseInt(arrayItems[1])) {
                                res.success.push(false);
                                res.messages.push(`El campo "${key}" ha de tener como mínimo ${arrayItems[1]} caracteres`);
                            } else {
                                res.validated[key] = data[key];
                            }
                        }
                        break;
                    case 'max':
                        if (data[key]) {
                            if (data[key].length > parseInt(arrayItems[1])) {
                                res.success.push(false);
                                res.messages.push(`El campo "${key}" ha de tener como máximo ${arrayItems[1]} caracteres`);
                            } else {
                                res.validated[key] = data[key];
                            }
                        }
                        break;
                    case 'color':
                        if (data[key]) {
                            if (!data[key].match(/^(#[a-fA-F0-9]{6})$/i)) {
                                res.success.push(false);
                                res.messages.push(`El campo "${key}" ha de tener formato hexadecimal, por ejemplo, #FFCC00`);
                            } else {
                                res.validated[key] = data[key];
                            }
                        }
                        break;
                    case 'array':
                        if (data[key]) {
                            if (!Array.isArray(data[key])) {
                                res.success.push(false);
                                res.messages.push(`El campo "${key}" debe ser un array`);
                            } else {
                                res.validated[key] = data[key];
                            }
                        }
                        break;
                    case 'number':
                        if (data[key]) {
                            if (!Number.isInteger(parseInt(data[key]))) {
                                res.success.push(false);
                                res.messages.push(`El campo "${key}" debe ser un numero`);
                            } else {
                                res.validated[key] = data[key];
                            }
                        }
                        break;
                    case 'boolean':
                        if (data[key]) {
                            if (typeof (!!data[key]) !== 'boolean') {
                                res.success.push(false);
                                res.messages.push(`El campo "${key}" debe ser verdadero o falso`);
                            } else {
                                res.validated[key] = data[key];
                            }
                        }
                        break;
                    case 'required_if':
                        if (arrayItems[1] === '1' && g.valueFailsRequiredValidation(data[key])) {
                            res.success.push(false);
                            res.messages.push(`El campo "${key}" es obligatorio en esta situación`);
                        } else {
                            res.validated[key] = data[key] ?? null;
                        }
                        break;
                    default:
                        res.success.push(false);
                        res.messages.push(`La validación introducida no esta controlada`);
                }
            });

        });

        return {success: (!res.success.includes(false)), messages: res.messages, validated: res.validated};
    }

    static strIncludesAny(str: string, values: string[]) {
        let res = false;
        values.forEach(value => {
            if (str.includes(value)) {
                res = true;
            }
        });
        return res;
    }

    static eventClosest(event: Event, selectors: string): HTMLElement | null {
        return (event?.target as HTMLElement).closest(selectors);
    }

    static showPageLoader() {
        const body = document.querySelector('body');
        body?.classList.add('overlay-body');
        // TODO Canals - (tailwind) pasar a componente
        body?.insertAdjacentHTML('beforeend', '<div id="page-loader" class="overlay"><div class="loader-container"><div class="loader"></div></div></div>');
    }

    static removePageLoader() {
        const body = document.querySelector('body');
        const loader = document.querySelector('#page-loader');
        body?.classList.remove('overlay-body');
        loader?.remove();
    }

    static reloadPage() {
        g.showPageLoader();
        location.reload();
    }

    static startTooltips() {
        import('bootstrap').then(bootstrap => {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
        }).catch(error => {
            console.error('Tabulator-tables no está instalado o no se pudo cargar.', error);
        });
    }

    static addSpinner(selectors: string | HTMLElement | NodeListOf<HTMLElement> | null, size: 'sm' | 'md' = 'md') {
        if (selectors === null) return;

        // TODO Canals - (tailwind) pasar a componente
        const sizeClass = (size === 'sm') ? 'spinner-border-sm' : '';
        const spinnerHtml = `<div class="spinner-border ${sizeClass}" data-added-in="general" role="status"><span class="visually-hidden">Loading...</span></div>`;
        if (selectors instanceof HTMLElement) {
            selectors.insertAdjacentHTML('afterbegin', spinnerHtml);
        } else if (selectors instanceof NodeList) {
            selectors.forEach((item) => {
                item.insertAdjacentHTML('afterbegin', spinnerHtml);
            });
        } else {
            const elements = document.querySelectorAll<HTMLElement>(selectors);
            elements.forEach(item => {
                item.insertAdjacentHTML('afterbegin', spinnerHtml);
            });
        }
    }

    static removeSpinner(selectors: string | HTMLElement | NodeListOf<HTMLElement> | null) {
        if (selectors === null) return;

        if (selectors instanceof HTMLElement) {
            selectors.querySelector('[data-added-in="general"]')?.remove();
        } else if (selectors instanceof NodeList) {
            selectors.forEach((item) => {
                item.querySelector('[data-added-in="general"]')?.remove();
            });
        } else {
            const elements = document.querySelectorAll<HTMLElement>(selectors);
            elements.forEach(item => {
                item.querySelector('[data-added-in="general"]')?.remove();
            });
        }
    }

    static consoleInfo(message?: any) {
        if (message) console.info(`%c INFO - ${message}`, 'background: #222; color: #bada55; padding: 0.5rem');
    }

    // Función para comprobar si un valor es truthy
    static isTruthy(value: any) {
        return !!value;
    }

    // Función para comprobar si un valor es falsy
    static isFalsy(value: any) {
        return !value;
    }

    // Función para comprobar si un valor es nullish (null o undefined)
    static isNullish(value: any) {
        return value === null || value === undefined;
    }

    /*static pluck(array: any[], key: string) {
        return array.map(i => i[key]);
    }

    static pluck(array: Record<any, any>[], value: string, key: string) {
        return array.reduce((acc, obj) => {
            acc[obj[key]] = obj[value];
            return acc;
        }, {});
    }*/

    static dataGet(target: any, key: Key, defaultValue: any = null): any {
        if (key === null) {
            return target;
        }

        const keys = Array.isArray(key) ? key : key.split('.');

        for (let i = 0; i < keys.length; i++) {
            const segment = keys[i];

            if (segment === null) {
                return target;
            }

            if (segment === '*') {
                if (typeof target[Symbol.iterator] !== 'function') {
                    return defaultValue;
                }

                const result: any[] = [];
                for (const item of target) {
                    result.push(g.dataGet(item, keys.slice(i + 1)));
                }
                return result.flat();
            }

            if (target && (typeof target === 'object' || Array.isArray(target)) && segment in target) {
                target = target[segment];
            } else {
                return defaultValue;
            }
        }

        return target;
    }

    static explodePluckParameters(value: Value, key: Key): [Value, Key] {
        const valueArray = typeof value === 'string' ? value.split('.') : value;
        const keyArray = key === null || Array.isArray(key) ? key : key.split('.');
        return [valueArray, keyArray];
    }


    static pluck(array: Record<any, any>[], value: Key): any[];
    static pluck(array: Record<any, any>[], value: Key, key: Key): Record<any, any>;
    static pluck(array: Record<any, any>[], value: Value, key: Key = null): any {
        const results: any = key === null ? [] : {};
        [value, key] = g.explodePluckParameters(value, key);

        for (const item of array) {
            const itemValue = g.dataGet(item, value);

            if (key === null) {
                results.push(itemValue);
            } else {
                let itemKey = g.dataGet(item, key);

                if (itemKey && typeof itemKey === 'object' && typeof itemKey.toString === 'function') {
                    itemKey = itemKey.toString();
                }

                results[itemKey] = itemValue;
            }
        }

        return results;
    }

    static slugify(text: string, separator: string = '-'): string {
        if (!text) return '';

        // Normaliza el texto a formato NFD y remueve los diacríticos
        text = text.normalize("NFD").replace(/\p{Diacritic}/gu, "");

        // Convierte a minúsculas, reemplaza caracteres no alfanuméricos con el separador
        text = text.toLowerCase()
            .replace(/[^a-z0-9]+/g, separator) // Reemplaza caracteres no alfanuméricos por el separador
            .replace(new RegExp(`^${separator}|${separator}$`, 'g'), ''); // Elimina separadores extra

        return text;
    }

    static getHiddenClass()
    {
        return __const('VITE_TS_USE_BOOSTRAP_CLASSES') ? 'd-none' : 'hidden';
    }

    static mergeTailwindClasses (defaultCls: string, extraCls: string) {
        const defaultArray = defaultCls.split(' ').filter(Boolean);
        const extraArray = extraCls.split(' ').filter(Boolean);
        return [
            ...defaultArray.filter(cls => !extraArray.some(extra => extra.startsWith(cls.split('-')[0]))),
            ...extraArray
        ].join(' ');
    }

    static debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        return (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
            if (timeoutId) clearTimeout(timeoutId);

            return new Promise((resolve, reject) => {
                timeoutId = setTimeout(async () => {
                    try {
                        const result = await fn(...args);
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                }, delay);
            });
        };
    }
}
